import React, { useEffect, useMemo, useState } from 'react'
import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import TableContainer from '@mui/material/TableContainer'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import ToggleButton from '@mui/material/ToggleButton'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Paper from '@mui/material/Paper'
import GoogleMapReact from 'google-map-react'
import PropTypes from 'prop-types'

import globalConfig from '../../config/globalConfig'

const SECTION_LABELS = {
    intVientoMax: 'Racha',
    intRafaga: 'Ráfagas',
    intViento: 'Viento medio',
    tempAire: 'Temperatura del aire',
    tempAireMax: 'Temperatura máxima',
    tempAireMin: 'Temperatura mínima',
}

const TOP_RANK_LIMIT = 10
const COMBINED_KEYS = ['intVientoMax', 'intRafaga']
const WIND_KEYS = ['intVientoMax', 'intRafaga', 'intViento']
const KNOT_TO_KMH = 1.852

const DEFAULT_MAP_CENTER = {
    lat: globalConfig.CENTRO_URUGUAY?.[0] ?? -33,
    lng: globalConfig.CENTRO_URUGUAY?.[1] ?? -56,
}

const MARKER_HEADER_COLOR = '#6fa8ff'
const MARKER_BORDER_COLOR = '#3b6edc'

const formatSectionTitle = (key) => {
    if (SECTION_LABELS[key]) return SECTION_LABELS[key]
    const cleaned = key.replace(/_/g, ' ')
    return cleaned.charAt(0).toUpperCase() + cleaned.slice(1)
}

const getStationName = (station) =>
    station?.displayName ||
    station?.displayNamePublic ||
    station?.nombre ||
    station?.idStr ||
    '—'

const normalizeFechaEntry = (entry, fallbackIndex = 0) => {
    if (!entry && entry !== 0) return null
    if (typeof entry === 'number' && Number.isFinite(entry)) {
        return entry
    }
    if (typeof entry === 'string') return entry
    if (Array.isArray(entry) && entry.length > 0) {
        const idx =
            typeof fallbackIndex === 'number' && fallbackIndex >= 0
                ? fallbackIndex
                : 0
        return normalizeFechaEntry(entry[idx] ?? entry[0])
    }
    if (
        typeof entry === 'object' &&
        entry !== null &&
        typeof entry.fecha === 'string'
    ) {
        return entry.fecha
    }
    if (
        typeof entry === 'object' &&
        entry !== null &&
        Array.isArray(entry.datos)
    ) {
        return normalizeFechaEntry(entry.datos, fallbackIndex)
    }
    return null
}

const buildRows = (sectionData, sectionDates) => {
    if (!sectionData) return { rows: [], min: null, max: null }
    const estaciones = sectionData.estaciones || []
    const matriz = sectionData.observaciones?.[0]?.datos || []
    const dateMatrix = sectionDates?.observaciones?.[0]?.datos || []
    const fallbackFechas = sectionDates?.fechas || []

    const resolveFecha = (stationIndex, valueIndex) => {
        if (valueIndex !== undefined && valueIndex !== -1) {
            const stationDates = dateMatrix[stationIndex]
            if (Array.isArray(stationDates)) {
                const stationValueDate = normalizeFechaEntry(
                    stationDates[valueIndex],
                    valueIndex
                )
                if (stationValueDate) return stationValueDate
            }
        }

        if (Array.isArray(fallbackFechas)) {
            if (valueIndex !== undefined && valueIndex !== -1) {
                const columnEntry = fallbackFechas[valueIndex]
                const columnDate = normalizeFechaEntry(
                    columnEntry,
                    stationIndex ?? 0
                )
                if (columnDate) return columnDate
            }

            const stationEntry = fallbackFechas[stationIndex]
            const stationDate = normalizeFechaEntry(
                stationEntry,
                valueIndex ?? 0
            )
            if (stationDate) return stationDate

            return normalizeFechaEntry(fallbackFechas[0])
        }

        return null
    }

    const rows = estaciones.map((station, index) => {
        const serie = matriz[index] || []
        let primaryValue = null
        let columnIndex = -1
        for (let i = 0; i < serie.length; i += 1) {
            const value = serie[i]
            if (typeof value === 'number' && !Number.isNaN(value)) {
                primaryValue = value
                columnIndex = i
                break
            }
        }

        const hasValue = typeof primaryValue === 'number'

        return {
            id: station?.idStr || station?.id || index,
            nombre: getStationName(station),
            valor: primaryValue,
            fecha: hasValue ? resolveFecha(index, columnIndex) : null,
            lat: station?.latitud ?? null,
            lng: station?.longitud ?? null,
        }
    })

    const validRows = rows.filter((row) => typeof row.valor === 'number')
    const sorted = validRows.sort((a, b) => b.valor - a.valor)
    const limited = sorted.slice(0, TOP_RANK_LIMIT).map((row, index) => ({
        ...row,
        rank: index + 1,
    }))
    const values = limited.map((row) => row.valor)
    const min = values.length ? Math.min(...values) : null
    const max = values.length ? Math.max(...values) : null
    return { rows: limited, min, max }
}

const formatValor = (valor) => {
    if (valor === null || valor === undefined) return '—'
    if (typeof valor === 'number') {
        if (Math.abs(valor) >= 100) return valor.toFixed(0)
        return valor.toFixed(1)
    }
    return valor
}

const createColorScale = () => () => MARKER_HEADER_COLOR

const MILLIS_PER_DAY = 24 * 60 * 60 * 1000
const DELPHI_EPOCH_MS = Date.UTC(1899, 11, 30)

const parseFechaValue = (fecha) => {
    if (fecha === null || fecha === undefined) return null
    if (typeof fecha === 'number' && Number.isFinite(fecha)) {
        const millis = DELPHI_EPOCH_MS + fecha * MILLIS_PER_DAY
        return new Date(millis)
    }
    if (typeof fecha === 'string') {
        const parsed = new Date(fecha)
        if (!Number.isNaN(parsed.getTime())) return parsed
        const numeric = Number(fecha)
        if (Number.isFinite(numeric)) {
            const millis = DELPHI_EPOCH_MS + numeric * MILLIS_PER_DAY
            return new Date(millis)
        }
    }
    if (fecha instanceof Date) return fecha
    return null
}

const adjustForPeriodicity = (date, periodicidad) => {
    if (!date || !periodicidad) return date
    if (periodicidad === 4) {
        const adjusted = new Date(date.getTime())
        const minutes = adjusted.getMinutes()
        let roundedMinutes = Math.round(minutes / 10) * 10
        if (roundedMinutes >= 60) {
            adjusted.setHours(adjusted.getHours() + 1)
            roundedMinutes -= 60
        }
        adjusted.setMinutes(roundedMinutes, 0, 0)
        return adjusted
    }
    return date
}

const formatFecha = (fecha, periodicidad) => {
    const resolvedDate = parseFechaValue(fecha)
    if (!resolvedDate) {
        return ''
    }
    const adjustedDate = adjustForPeriodicity(resolvedDate, periodicidad)
    return adjustedDate.toLocaleString('es-UY', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    })
}

const MapMarker = ({
    color,
    station,
    value,
    fecha,
    periodicidad,
    unit,
    rank,
}) => {
    const formattedDate = formatFecha(fecha, periodicidad)
    const formattedValue =
        formatValor(value) + (unit ? ` ${unit}` : '')
    const stationLabel = station.toUpperCase()
    return (
        <Box
            sx={{
                position: 'relative',
                transform: 'translate(-50%, -100%)',
                pointerEvents: 'none',
                display: 'inline-flex',
                flexDirection: 'column',
                alignItems: 'center',
                zIndex: 100 - (rank || 0),
            }}
        >
            <Paper
                elevation={6}
                sx={{
                    p: 1,
                    minWidth: 110,
                    maxWidth: 160,
                    bgcolor: '#ffffff',
                    color: '#082338',
                    borderRadius: '8px 8px 2px 2px',
                    textAlign: 'center',
                    pointerEvents: 'auto',
                    border: `2px solid ${MARKER_BORDER_COLOR}`,
                    mb: '-2px',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.5,
                        mb: 0.75,
                        mt: -1,
                        mx: -1,
                        bgcolor: color,
                        color: '#ffffff',
                        borderRadius: '7px 7px 4px 4px',
                        px: 1,
                        py: 0.75,
                    }}
                >
                    <Box
                        sx={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            bgcolor: '#ffffff',
                            color: '#082338',
                            fontSize: 12,
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 0 4px rgba(0,0,0,0.2)',
                            flexShrink: 0,
                            border: '1px solid rgba(128,128,128,0.4)',
                        }}
                    >
                        {rank}
                    </Box>
                    <Typography
                        variant="caption"
                        fontWeight={700}
                        color="#ffffff"
                    >
                        {stationLabel}
                    </Typography>
                </Box>
                <Typography variant="subtitle2" fontWeight={700}>
                    {formattedValue}
                </Typography>
                {formattedDate && (
                    <Typography variant="caption" color="inherit">
                        {formattedDate}
                    </Typography>
                )}
            </Paper>
            <Box
                sx={{
                    width: 0,
                    height: 0,
                    borderLeft: '14px solid transparent',
                    borderRight: '14px solid transparent',
                    borderTop: `16px solid ${MARKER_BORDER_COLOR}`,
                    position: 'absolute',
                    top: '100%',
                    left: '50%',
                    transform: 'translate(-50%, 0)',
                    zIndex: 0,
                }}
            />
        </Box>
    )
}


MapMarker.propTypes = {
    color: PropTypes.string.isRequired,
    station: PropTypes.string.isRequired,
    value: PropTypes.number.isRequired,
    fecha: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    periodicidad: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.oneOf([null]),
    ]),
    unit: PropTypes.string,
    rank: PropTypes.number,
}


const ResumenAdv = () => {
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [activeGroupId, setActiveGroupId] = useState(null)
    const [activeMapSectionKey, setActiveMapSectionKey] = useState(null)
    const [activeView, setActiveView] = useState('map')

    useEffect(() => {
        const controller = new AbortController()
        const publicUrl = (process.env.PUBLIC_URL || '').replace(/\/$/, '')

        fetch(`${publicUrl}/resumen_evento.json`, { signal: controller.signal })
            .then((response) => {
                if (!response.ok) {
                    throw new Error('No se pudo cargar el archivo resumen.')
                }
                return response.json()
            })
            .then((json) => {
                setData(json)
                setError(null)
            })
            .catch((fetchError) => {
                if (fetchError.name !== 'AbortError') {
                    setError(fetchError.message)
                }
            })
            .finally(() => {
                setLoading(false)
            })

        return () => controller.abort()
    }, [])

    const sections = useMemo(() => {
        if (!data) return []
        return Object.entries(data)
            .filter(([key]) => key !== 'estado' && data[key]?.datos)
            .map(([key, value]) => {
                const sectionData = value.datos
                const sectionDates = value.fechas
                const variable = sectionData.variables?.[0]
                const { rows, min, max } = buildRows(sectionData, sectionDates)
                const isWind = WIND_KEYS.includes(key)
                const adjustedRows = isWind
                    ? rows.map((row) => ({
                          ...row,
                          valor: row.valor * KNOT_TO_KMH,
                      }))
                    : rows
                const rangeMin =
                    isWind && min !== null ? min * KNOT_TO_KMH : min
                const rangeMax =
                    isWind && max !== null ? max * KNOT_TO_KMH : max
                return {
                    key,
                    title: formatSectionTitle(key),
                    description: variable?.nombre || '',
                    unidad: isWind ? 'km/h' : variable?.unidad || '',
                    periodicidad: variable?.periodicidad || null,
                    rows: adjustedRows,
                    colorScale: createColorScale(rangeMin, rangeMax),
                    range: { min: rangeMin, max: rangeMax },
                }
            })
            .filter((section) => section.rows.length > 0)
    }, [data])

    const sectionMap = useMemo(() => {
        const map = new Map()
        sections.forEach((section) => map.set(section.key, section))
        return map
    }, [sections])

    const sectionGroups = useMemo(() => {
        if (sections.length === 0) return []
        const map = new Map()
        sections.forEach((section) => map.set(section.key, section))
        const used = new Set()
        const groups = []
        if (COMBINED_KEYS.every((key) => map.has(key))) {
            groups.push({
                id: 'intVientoMax-intRafaga',
                label: 'Viento y Ráfagas',
                sectionKeys: COMBINED_KEYS,
            })
            COMBINED_KEYS.forEach((key) => used.add(key))
        }
        sections.forEach((section) => {
            if (used.has(section.key)) return
            groups.push({
                id: section.key,
                label: section.title,
                sectionKeys: [section.key],
            })
        })
        return groups
    }, [sections])

    useEffect(() => {
        if (sectionGroups.length === 0) {
            if (activeGroupId !== null) setActiveGroupId(null)
            if (activeMapSectionKey !== null) setActiveMapSectionKey(null)
            if (activeView !== 'map') setActiveView('map')
            return
        }
        const currentGroup =
            sectionGroups.find((group) => group.id === activeGroupId) ||
            sectionGroups[0]
        if (currentGroup.id !== activeGroupId) {
            setActiveGroupId(currentGroup.id)
            setActiveView('map')
        }
        const preferredKey = currentGroup.sectionKeys.includes(
            activeMapSectionKey
        )
            ? activeMapSectionKey
            : currentGroup.sectionKeys[0]
        if (preferredKey !== activeMapSectionKey) {
            setActiveMapSectionKey(preferredKey)
        }
    }, [sectionGroups, activeGroupId, activeMapSectionKey])

    const activeGroup =
        sectionGroups.find((group) => group.id === activeGroupId) || null

    const mapSection =
        (activeMapSectionKey && sectionMap.get(activeMapSectionKey)) || null

    const tableSections = activeGroup
        ? activeGroup.sectionKeys
              .map((key) => sectionMap.get(key))
              .filter(Boolean)
        : []

    const mapCoordinates = useMemo(() => {
        if (!mapSection || mapSection.rows.length === 0) return []
        return mapSection.rows
            .filter(
                (row) =>
                    typeof row.lat === 'number' && typeof row.lng === 'number'
            )
            .map((row) => ({ lat: row.lat, lng: row.lng }))
    }, [mapSection])

    const mapCenter = useMemo(() => {
        if (mapCoordinates.length === 0) return DEFAULT_MAP_CENTER
        const avgLat =
            mapCoordinates.reduce((sum, row) => sum + row.lat, 0) /
            mapCoordinates.length
        const avgLng =
            mapCoordinates.reduce((sum, row) => sum + row.lng, 0) /
            mapCoordinates.length
        return { lat: avgLat, lng: avgLng }
    }, [mapCoordinates])

    const mapBounds = useMemo(() => {
        if (mapCoordinates.length === 0) return null
        return mapCoordinates.reduce(
            (acc, { lat, lng }) => ({
                north: Math.max(acc.north, lat),
                south: Math.min(acc.south, lat),
                east: Math.max(acc.east, lng),
                west: Math.min(acc.west, lng),
            }),
            {
                north: -Infinity,
                south: Infinity,
                east: -Infinity,
                west: Infinity,
            }
        )
    }, [mapCoordinates])

    const mapZoom = mapCoordinates.length > 0 ? 6 : 5

    const [mapApi, setMapApi] = useState(null)

    const handleMapSectionChange = (_, value) => {
        if (value) {
            setActiveMapSectionKey(value)
        }
    }

    const handleTabChange = (_, value) => {
        setActiveGroupId(value)
        const group = sectionGroups.find((item) => item.id === value)
        if (group && group.sectionKeys.length > 0) {
            setActiveMapSectionKey(group.sectionKeys[0])
        }
        setActiveView('map')
    }

    const handleViewChange = (_, value) => {
        if (value) setActiveView(value)
    }

    useEffect(() => {
        if (!mapApi) return
        const { map, maps } = mapApi
        if (!map || !maps) return
        if (!mapBounds) {
            map.setCenter(mapCenter)
            map.setZoom(mapZoom)
            return
        }
        const bounds = new maps.LatLngBounds()
        mapCoordinates.forEach(({ lat, lng }) =>
            bounds.extend(new maps.LatLng(lat, lng))
        )
        map.fitBounds(bounds, { top: 30, right: 30, bottom: 30, left: 30 })
    }, [mapApi, mapBounds, mapCenter, mapCoordinates, mapZoom])

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: '#ffffff',
                py: { xs: 0, md: 0 },
                px: { xs: 0, md: 0 },
                overflowY: 'auto',
                fontFamily: '"Nunito", "Segoe UI", sans-serif',
            }}
        >
            <Container maxWidth={false} disableGutters>

                {loading && (
                    <Stack alignItems="center" sx={{ py: 6 }}>
                        <CircularProgress color="primary" />
                    </Stack>
                )}

                {error && (
                    <Alert severity="error" sx={{ my: 3 }}>
                        {error}
                    </Alert>
                )}

                {!loading && !error && sectionGroups.length === 0 && (
                    <Typography variant="body2" sx={{ mt: 3 }}>
                        No se detectaron variables en el archivo de resumen.
                    </Typography>
                )}

                {!loading &&
                    !error &&
                    sectionGroups.length > 0 &&
                    activeGroup &&
                    mapSection && (
                        <>
                            <Card
                                sx={{
                                    mt: 0,
                                    borderRadius: 0,
                                    boxShadow: 'none',
                                    border: 'none',
                                    background: '#ffffff',
                                    overflow: 'visible',
                                    maxWidth: '100%',
                                }}
                            >
                                <CardContent sx={{ px: { xs: 0, md: 2 }, pb: 3 }}>
                                    <Tabs
                                        value={activeGroupId}
                                        onChange={handleTabChange}
                                        indicatorColor="primary"
                                        variant="scrollable"
                                        sx={{ mb: 2, minHeight: 48 }}
                                    >
                                        {sectionGroups.map((group) => (
                                            <Tab
                                                key={group.id}
                                                value={group.id}
                                                label={group.label}
                                                sx={{
                                                    textTransform: 'none',
                                                    color: '#212937',
                                                    fontWeight: 600,
                                                    fontSize: 16,
                                                    borderRadius: 2,
                                                    mr: 1,
                                                    px: 2,
                                                    minHeight: 42,
                                                    bgcolor: 'transparent',
                                                    '&.Mui-selected': {
                                                        color: '#ffffff',
                                                        bgcolor: '#0d6efd',
                                                        borderRadius: 2,
                                                        boxShadow:
                                                            '0 6px 14px rgba(13,110,253,0.35)',
                                                    },
                                                }}
                                            />
                                        ))}
                                    </Tabs>

                                    <Card
                                        sx={{
                                            borderRadius: 0,
                                            boxShadow: 'none',
                                            border: 'none',
                                            background: '#ffffff',
                                        }}
                                    >
                                        <CardContent>
                                    <Tabs
                                        value={activeView}
                                        onChange={handleViewChange}
                                        indicatorColor="primary"
                                        textColor="primary"
                                        sx={{
                                            bgcolor: '#f5f8fc',
                                            borderRadius: 2,
                                            border: '1px solid rgba(0, 91, 159, 0.15)',
                                            px: 1,
                                            mb: 3,
                                        }}
                                    >
                                        <Tab
                                            value="map"
                                            label="Valores en Mapa"
                                            sx={{
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                fontSize: 16,
                                            }}
                                        />
                                        <Tab
                                            value="tables"
                                            label="Tabla"
                                            sx={{
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                fontSize: 16,
                                            }}
                                        />
                                    </Tabs>

                                    {activeView === 'map' && (
                                        <>
                                            {activeGroup.sectionKeys.length > 1 && (
                                                <ToggleButtonGroup
                                                    size="small"
                                                    value={activeMapSectionKey}
                                                    exclusive
                                                    onChange={handleMapSectionChange}
                                                    sx={{
                                                        alignSelf: { xs: 'flex-start', md: 'center' },
                                                        flexWrap: 'wrap',
                                                        gap: 1,
                                                        mb: 2,
                                                    }}
                                                >
                                                    {activeGroup.sectionKeys.map((key) => {
                                                        const section =
                                                            sectionMap.get(key)
                                                        return (
                                                            <ToggleButton
                                                                key={key}
                                                                value={key}
                                                                sx={{
                                                                    textTransform: 'none',
                                                                    borderRadius: 2,
                                                                    borderColor:
                                                                        'rgba(0, 91, 159, 0.3)',
                                                                }}
                                                            >
                                                                {section?.title ||
                                                                    formatSectionTitle(key)}
                                                            </ToggleButton>
                                                        )
                                                    })}
                                                </ToggleButtonGroup>
                                            )}
                                            <Box
                                                sx={{
                                                    height: {
                                                        xs: 'calc(100vh - 140px)',
                                                        md: 540,
                                                        lg: 640,
                                                    },
                                                    borderRadius: { xs: 0, md: 3 },
                                                    overflow: 'hidden',
                                                    border: {
                                                        xs: 'none',
                                                        md: '1px solid rgba(15, 23, 42, 0.08)',
                                                    },
                                                    mx: { xs: 0, md: 0 },
                                                }}
                                            >
                                                <GoogleMapReact
                                                    bootstrapURLKeys={{
                                                        key: 'AIzaSyD0_rAVqKBVyyA-6kB3hMxNgmMyLRFlno8',
                                                    }}
                                                    defaultCenter={mapCenter}
                                                    defaultZoom={mapZoom}
                                                    options={{
                                                        fullscreenControl: false,
                                                        streetViewControl: false,
                                                        mapTypeControl: false,
                                                    }}
                                                    yesIWantToUseGoogleMapApiInternals
                                                    onGoogleApiLoaded={({
                                                        map,
                                                        maps,
                                                    }) => setMapApi({ map, maps })}
                                                >
                                                    {mapSection.rows.map((row) => {
                                                        if (
                                                            typeof row.lat !== 'number' ||
                                                            typeof row.lng !== 'number'
                                                        ) {
                                                            return null
                                                        }
                                                        return (
                                                            <MapMarker
                                                                key={`marker-${mapSection.key}-${row.id}`}
                                                                lat={row.lat}
                                                                lng={row.lng}
                                                                station={row.nombre}
                                                                value={row.valor}
                                                                fecha={row.fecha}
                                                                periodicidad={
                                                                    mapSection.periodicidad
                                                                }
                                                                color={mapSection.colorScale(
                                                                    row.valor
                                                                )}
                                                                unit={mapSection.unidad}
                                                                rank={row.rank}
                                                            />
                                                        )
                                                    })}
                                                </GoogleMapReact>
                                            </Box>
                                        </>
                                    )}

                                    {activeView === 'tables' && (
                                        <Grid
                                            container
                                            spacing={{ xs: 3, md: 2 }}
                                            sx={{ mt: 1 }}
                                        >
                                            {tableSections.map((section) => (
                                                <Grid
                                                    item
                                                    xs={12}
                                                    md={12}
                                                    key={section.key}
                                                    sx={{
                                                        '@media (min-width: 1200px)': {
                                                            flexBasis: '100%',
                                                            maxWidth: '100%',
                                                        },
                                                    }}
                                                >
                                                    <Card
                                                        sx={{
                                                            height: '100%',
                                                            borderRadius: 3,
                                                            boxShadow: 5,
                                                            border: '1px solid rgba(0, 91, 159, 0.12)',
                                                            background:
                                                                'linear-gradient(180deg, rgba(255,255,255,0.98), rgba(223,238,252,0.95))',
                                                        }}
                                                    >
                                                        <CardContent>
                                                            <Typography
                                                                variant="h6"
                                                                component="h2"
                                                                gutterBottom
                                                            >
                                                                {section.title}
                                                            </Typography>
                                                            {section.description && (
                                                                <Typography
                                                                    variant="body2"
                                                                    color="text.secondary"
                                                                >
                                                                    {section.description}
                                                                    {section.unidad
                                                                        ? ` (${section.unidad})`
                                                                        : ''}
                                                                </Typography>
                                                            )}
                                                <TableContainer
                                                    sx={{
                                                        mt: 2,
                                                        borderRadius: 2,
                                                        border:
                                                            '1px solid rgba(15, 23, 42, 0.08)',
                                                        width: 'calc(100% + 32px)',
                                                        ml: -2,
                                                        mr: -2,
                                                        fontSize: 17,
                                                        '& .MuiTableCell-root': {
                                                            fontSize: 17,
                                                            py: 1.25,
                                                        },
                                                    }}
                                                >
                                                    <Table
                                                        size="small"
                                                        sx={{
                                                            width: '100%',
                                                        }}
                                                    >
                                                                    <TableHead>
                                                                        <TableRow>
                                                                            <TableCell>
                                                                                Estación
                                                                            </TableCell>
                                                                            <TableCell>
                                                                                Fecha
                                                                            </TableCell>
                                                                            <TableCell align="right">
                                                                                Valor
                                                                                {section.unidad
                                                                                    ? ` (${section.unidad})`
                                                                                    : ''}
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    </TableHead>
                                                                    <TableBody>
                                                                        {section.rows.map((row) => (
                                                                            <TableRow key={row.id}>
                                                                                <TableCell>
                                                                                    <Typography
                                                                                        variant="body2"
                                                                                        fontWeight={700}
                                                                                    >
                                                                                        {'#'}
                                                                                        {row.rank}{' '}
                                                                                        ·{' '}
                                                                                        {row.nombre.toUpperCase()}
                                                                                    </Typography>
                                                                                </TableCell>
                                                                                <TableCell>
                                                                                    {formatFecha(
                                                                                        row.fecha,
                                                                                        section.periodicidad
                                                                                    )}
                                                                                </TableCell>
                                                                                <TableCell align="right">
                                                                                    {formatValor(row.valor)}
                                                                                </TableCell>
                                                                            </TableRow>
                                                                        ))}
                                                                    </TableBody>
                                                                </Table>
                                                            </TableContainer>
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    )}
                                </CardContent>
                            </Card>
                        </CardContent>
                    </Card>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ display: 'block', px: { xs: 2, md: 2 }, pb: 3 }}
                    >
                        NOTA: La información presentada se basa en datos
                        preliminares de estaciones meteorológicas automáticas.
                    </Typography>
                        </>
                    )}
            </Container>
        </Box>
    )
}

export default ResumenAdv
